/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useEffect } from 'react';
import FocusLock from 'react-focus-lock';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import Keys from '../../../types/Keys';

export interface ModalProps {
    children: JSX.Element;
    large?: boolean;
    mediumLarge?: boolean;
    medium?: boolean;
    small?: boolean;
    isShown: boolean;
    hide: () => void;
    focusLock?: boolean;
    dataTestId?: string;
    top?: boolean;
    verticalCenter?: boolean;
    elevated?: boolean;
}
export interface StyledProps {
    $large?: boolean;
    $mediumLarge?: boolean;
    $medium?: boolean;
    $small?: boolean;
    $top?: boolean;
    $verticalCenter?: boolean;
    $elevated?: boolean;
}

interface StyledModalProps {
    $styled?: string;
}

export const Modal: React.FC<ModalProps> = ({
    isShown,
    hide,
    large,
    mediumLarge,
    medium,
    small,
    children,
    focusLock,
    dataTestId,
    top,
    verticalCenter: verticalCenter,
    elevated,
}) => {
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === Keys.ESCAPE && isShown) {
                hide();
            }
        };

        isShown
            ? (document.body.style.overflow = 'hidden')
            : (document.body.style.overflow = 'unset');

        document.addEventListener('keydown', onKeyDown, false);
        if (isShown) document.documentElement.style.overflowY = 'hidden';

        return () => {
            if (isShown) document.documentElement.style.overflowY = 'initial';

            document.removeEventListener('keydown', onKeyDown, false);
            document.body.style.overflow = 'unset';
        };
    }, [isShown, hide]);

    const modal = (
        <>
            <Backdrop $elevated={elevated} onClick={hide} />
            <FocusLock disabled={!focusLock}>
                <Wrapper
                    data-testid={dataTestId}
                    $large={large}
                    $mediumLarge={mediumLarge}
                    $medium={medium}
                    $small={small}
                    $top={top}
                    $verticalCenter={verticalCenter}
                    $elevated={elevated}
                    aria-modal
                    tabIndex={-1}
                    role="dialog"
                >
                    <StyledModal>{children}</StyledModal>
                </Wrapper>
                <CloseButton $elevated={elevated} onClick={hide}>
                    <CloseLineIcon size="17" />
                </CloseButton>
            </FocusLock>
        </>
    );

    return isShown ? ReactDOM.createPortal(modal, document.body) : null;
};

const Wrapper = styled.div<StyledProps>`
    position: fixed;
    top: ${props =>
        props.$top ? '35%' : props.$mediumLarge && !props.$verticalCenter ? '10rem' : '50%'};
    left: 50%;
    ${props =>
        props.$large &&
        `
        height: calc(100vh - 15rem);
        min-width: 85vw;
        max-width: 144rem;
    `};

    ${props =>
        props.$mediumLarge &&
        `
        max-width: 80rem;
        width: 80vw;
    `};

    ${props =>
        props.$medium &&
        `
        width: 55rem;
    `};

    ${props =>
        props.$small &&
        `
        width: 45rem;
    `};

    ${props =>
        !props.$large &&
        !props.$mediumLarge &&
        !props.$medium &&
        !props.$small &&
        `
        max-height: calc(100vh - 30rem);
    `};
    outline: 0;
    border-radius: 0.8rem;
    transform: translate(-50%, -50%);
    background: ${props => props.theme.colors.light};
    z-index: ${props => (props.$elevated ? 20 : 10)};
`;

const Backdrop = styled.div<StyledProps>`
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: ${props => props.theme.colors.overlay};
    z-index: ${props => (props.$elevated ? 18 : 8)};
`;

const StyledModal = styled.div<StyledModalProps>`
    display: grid;
    grid-template-areas: 'header' 'content' 'error' 'footer';
    grid-template-rows: 6rem auto auto 8rem;
    height: 100%;
    margin: auto;
    ${props => props.$styled && 'padding: 3rem'};
    background: ${props => props.theme.colors.light};
    border-radius: 0.8rem;
    z-index: 10;
`;

const CloseButton = styled.button<StyledModalProps & StyledProps>`
    ${props =>
        !props.$styled &&
        `position: absolute; right: ${props.theme.layout.padding.large}; top: ${props.theme.layout.padding.large}`}
    right: ${props => props.theme.layout.padding.large};
    top: ${props => props.theme.layout.padding.large};
    background: transparent;
    border: none;
    border-radius: 0.3rem;
    z-index: ${props => (props.$elevated ? 21 : 1)};
    :hover {
        cursor: pointer;
        color: ${props => props.theme.colors.primary};
    }
    :focus {
        border: none;
        outline: 0;
    }
`;
